import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Modal,
} from "@mui/material";
import { Table } from "antd";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useRef, useState } from "react";
import LoadingBackdrop from "../components/general/LoadingBackdrop";
import ReusableButton from "../components/general/ReusableButton";
import ReusableSelect from "../components/general/ReusableSelect";
import AddNewCompetitionForCertificateModal from "../components/modal/AddNewCompetitionForCertificateModal";
import ConfirmDeleteModal from "../components/modal/ConfirmDeleteModal";
import FilterChips from "../components/ui/FilterChips";
import FilterDrawer from "../components/ui/FilterDrawer";
import PageHeader from "../components/ui/PageHeader";
import SearchInput from "../components/ui/SearchInput";
import TableCard from "../components/ui/TableCard";
import TableToolbar from "../components/ui/TableToolbar";
import { isAdmin } from "../functions/checkrole";
import { deleteCertificateConfiguration } from "../functions/deleteData";
import { cancelAction, formatDate } from "../functions/general";
import {
  filterConfiguredCertificateTemplates,
  getConfiguredNewCompetitionsInPrintCertificates,
  getCertificateTemplateAwardDownload,
  getCertificateTemplateAwardPreview,
} from "../functions/getData";
import {
  addNewCompetitionInPrintCertificates,
  updateCertificateConfiguration,
} from "../functions/postData";
import { showNotification } from "../functions/snackbar";

const ConfigureCertificate = () => {
  document.title = "Certificate Templates";
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [onAddCompetition, setOnAddCompetition] = useState(false);
  const [onEditCompetition, setOnEditCompetition] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [onDelete, setOnDelete] = useState(false);
  const [selectedConfigId, setSelectedConfigId] = useState(null);
  const cancelAddCompetition = () => cancelAction(setOnAddCompetition);
  const cancelEditCompetition = () => {
    setOnEditCompetition(false);
    setEditingConfig(null);
  };
  const [rawData, setRawData] = useState([]);
  const [formattedData, setFormattedData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filterCompetition, setFilterCompetition] = useState(null);
  const [filterCertificateType, setFilterCertificateType] = useState(null);
  const [competitionFilterOptions, setCompetitionFilterOptions] = useState([]);
  const [certificateTypeFilterOptions, setCertificateTypeFilterOptions] =
    useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [filtering, setFiltering] = useState(false);
  const [filterDraft, setFilterDraft] = useState({ competition: "", certificateType: "" });
  const pageSizeRef = useRef(pageSize);

  const formatData = (data) => {
    const result = [];
    data?.map((el) => {
      const formattedObj = {};
      const nestedRows = [];
      formattedObj.id = el?.id;
      formattedObj.configuration_name =
        el?.configuration_name ?? el?.name ?? "-";
      formattedObj.competition_name =
        el?.competition_name ?? el?.round_name ?? "-";
      formattedObj.competition_id = el?.competition_id ?? el?.round_id;
      formattedObj.certificate_type_display =
        (typeof el?.certificate_type === "object"
          ? el.certificate_type.filter_name ?? el.certificate_type.name
          : null) ??
        el?.certificate_type_name ??
        el?.round_type ??
        (el?.certificate_type != null ? String(el.certificate_type) : "-");
      const countries = el?.countries ?? [];
      const organizations = el?.organizations ?? [];
      const configRoles = el?.roles ?? [];
      formattedObj.countries_display =
        Array.isArray(countries) && countries.length
          ? countries
              .map((c) => (typeof c === "object" ? c?.name ?? c?.label : c))
              .join(", ")
          : "-";
      formattedObj.organizations_display =
        Array.isArray(organizations) && organizations.length
          ? organizations
              .map((o) => (typeof o === "object" ? o?.name ?? o?.label : o))
              .join(", ")
          : "-";
      const rolesDisplay =
        Array.isArray(configRoles) && configRoles.length
          ? configRoles
              .map((r) => (typeof r === "object" ? r?.name ?? r?.label : r))
              .join(", ")
          : "-";
      formattedObj.roles_display = rolesDisplay;
      formattedObj.configuration_date = el?.created_at ? el.created_at : "-";
      formattedObj.update_date = el?.updated_at ? el.updated_at : "-";
      const awards = el?.awards ?? el?.templates ?? [];
      awards.forEach((item) => {
        const formattedNestedObj = {};
        formattedNestedObj.award_id = item?.id;
        formattedNestedObj.award = item?.award ?? "-";
        formattedNestedObj.file_name = item?.file_name ?? "-";
        formattedNestedObj.roles =
          Array.isArray(item?.roles) && item.roles.length
            ? item.roles
                .map((r) => (typeof r === "object" ? r?.name ?? r?.label : r))
                .join(" - ")
            : rolesDisplay;
        formattedNestedObj.show_disclaimer =
          item?.show_disclaimer ?? false ? "Yes" : "No";
        formattedNestedObj.configuration_date = item?.created_at
          ? item.created_at
          : "-";
        formattedNestedObj.update_date = item?.updated_at
          ? item.updated_at
          : "-";
        formattedNestedObj.url = item?.url ?? item?.download_url;
        nestedRows.push(formattedNestedObj);
      });

      formattedObj.nestedData = nestedRows;
      result.push(formattedObj);
    });
    return result;
  };

  const listData = useCallback(
    async (page = 1, perPage = 10) => {
      setLoading(true);
      try {
        const params = {
          page,
          per_page: perPage,
          ...(searchText?.trim() && { search: searchText.trim() }),
          ...(filterCompetition?.value != null &&
            filterCompetition?.value !== "" && {
              competition: filterCompetition.value,
            }),
          ...(filterCertificateType !== null &&
            filterCertificateType !== undefined &&
            filterCertificateType !== "" && {
              certificate_type: filterCertificateType,
            }),
        };
        const res = await getConfiguredNewCompetitionsInPrintCertificates(
          params
        );
        const data = Array.isArray(res) ? res : res?.data ?? [];
        const meta = res?.meta ?? {};
        setRawData(data);
        setFormattedData(formatData(data));
        setTotalItems(meta.total ?? data.length);
      } finally {
        setLoading(false);
      }
    },
    [searchText, filterCompetition, filterCertificateType]
  );

  useEffect(() => {
    listData(currentPage, pageSize);
  }, [listData, currentPage, pageSize]);

  // Suppress ResizeObserver error - common issue with Ant Design tables
  useEffect(() => {
    const resizeObserverError = (e) => {
      if (
        e.message ===
          "ResizeObserver loop completed with undelivered notifications." ||
        e.message === "ResizeObserver loop limit exceeded"
      ) {
        e.stopImmediatePropagation();
      }
    };
    window.addEventListener("error", resizeObserverError);
    return () => {
      window.removeEventListener("error", resizeObserverError);
    };
  }, []);

  const approveNewCompetitionFunction = (formData) => {
    setLoading(true);
    addNewCompetitionInPrintCertificates(formData)
      .then((res) => {
        showNotification(
          "success",
          res?.message || "Configuration added successfully",
          enqueueSnackbar,
          closeSnackbar
        );
        listData(currentPage, pageSize);
      })
      .catch((err) => {
        showNotification(
          "error",
          err?.message || err?.errors || "Failed to add configuration",
          enqueueSnackbar,
          closeSnackbar
        );
      })
      .finally(() => {
        setLoading(false);
        setOnAddCompetition(false);
      });
  };

  const approveEditCompetitionFunction = (formData) => {
    if (!editingConfig?.id) return;
    setLoading(true);
    updateCertificateConfiguration(editingConfig.id, formData)
      .then((res) => {
        showNotification(
          "success",
          res?.message || "Configuration updated successfully",
          enqueueSnackbar,
          closeSnackbar
        );
        listData(currentPage, pageSize);
        cancelEditCompetition();
      })
      .catch((err) => {
        showNotification(
          "error",
          err?.message || err?.errors || "Failed to update configuration",
          enqueueSnackbar,
          closeSnackbar
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDeleteConfig = async (id) => {
    setLoading(true);
    deleteCertificateConfiguration(id)
      .then(() => {
        showNotification(
          "success",
          "Configuration deleted successfully",
          enqueueSnackbar,
          closeSnackbar
        );
        listData(currentPage, pageSize);
        setOnDelete(false);
        setSelectedConfigId(null);
      })
      .catch((err) => {
        showNotification(
          "error",
          err?.message || "Failed to delete configuration",
          enqueueSnackbar,
          closeSnackbar
        );
      })
      .finally(() => setLoading(false));
  };

  // Opens an award's PDF (fetched with auth) in a new tab
  const openAwardPdfInNewTab = (fetchPdf, awardId, loadingText) => {
    // Open the tab synchronously so the browser doesn't block it as a popup
    const pdfWindow = window.open("", "_blank");
    // Generating the PDF takes a few seconds, so show a message meanwhile
    if (pdfWindow) {
      pdfWindow.document.title = "Loading...";
      pdfWindow.document.body.innerHTML = `<p style="font-family: sans-serif; text-align: center; margin-top: 40vh; color: var(--color-primary);">${loadingText}</p>`;
    }
    setLoading(true);
    fetchPdf(awardId)
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(
          new Blob([blob], { type: "application/pdf" })
        );
        if (pdfWindow) pdfWindow.location.href = blobUrl;
        else window.open(blobUrl, "_blank");
        // Give the new tab time to load the PDF before releasing it
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
      })
      .catch(() => pdfWindow?.close())
      .finally(() => setLoading(false));
  };

  const handleDownloadTemplate = (row) =>
    openAwardPdfInNewTab(
      getCertificateTemplateAwardDownload,
      row.award_id,
      "Loading certificate template, please wait..."
    );

  const handlePrintWithDummyData = (row) =>
    openAwardPdfInNewTab(
      getCertificateTemplateAwardPreview,
      row.award_id,
      "Generating certificate preview, please wait..."
    );

  useEffect(() => {
    const loadFilterOptions = async () => {
      setLoadingFilters(true);
      try {
        const extractFilterList = (data) =>
          Array.isArray(data)
            ? data
            : data?.data ?? data?.results ?? data?.items ?? [];
        const [competitionRaw, certificateTypeRaw] = await Promise.all([
          filterConfiguredCertificateTemplates("competition", {}),
          filterConfiguredCertificateTemplates("certificate_type", {}),
        ]);
        const competitionList = extractFilterList(competitionRaw);
        const certificateTypeList = extractFilterList(certificateTypeRaw);
        setCompetitionFilterOptions(
          competitionList.map((item) => ({
            label: item.filter_name ?? item.name,
            value: item.filter_id ?? item.id,
          }))
        );
        setCertificateTypeFilterOptions(
          certificateTypeList.map((item) => ({
            label: item.filter_name ?? item.name,
            value: item.filter_id ?? item.id,
          }))
        );
      } finally {
        setLoadingFilters(false);
      }
    };
    loadFilterOptions();
  }, []);

  const parentColumns = [
    {
      title: "Configuration Name",
      dataIndex: "configuration_name",
      key: "configuration_name",
    },
    {
      title: "Competition Name",
      dataIndex: "competition_name",
      key: "competition_name",
    },
    {
      title: "Certificate Type",
      dataIndex: "certificate_type_display",
      key: "certificate_type_display",
    },
    {
      title: "Countries",
      dataIndex: "countries_display",
      key: "countries_display",
    },
    {
      title: "Organizations",
      dataIndex: "organizations_display",
      key: "organizations_display",
    },
    {
      title: "Roles",
      dataIndex: "roles_display",
      key: "roles_display",
    },
    {
      title: "Configuration date",
      dataIndex: "configuration_date",
      key: "configuration_date",
    },
    {
      title: "Update date",
      dataIndex: "update_date",
      key: "update_date",
    },
    ...(isAdmin()
      ? [
          {
            title: "Actions",
            key: "actions",
            width: 120,
            fixed: "right",
            render: (_, record) => (
              <Box display="flex" gap={1} alignItems="center">
                <EditIcon
                  style={{ cursor: "pointer", color: "var(--color-primary)" }}
                  onClick={() => {
                    const raw =
                      rawData.find((r) => r.id === record.id) || record;
                    setEditingConfig(raw);
                    setOnEditCompetition(true);
                  }}
                />
                <DeleteIcon
                  style={{ cursor: "pointer", color: "var(--color-danger)" }}
                  onClick={() => {
                    setSelectedConfigId(record.id);
                    setOnDelete(true);
                  }}
                />
              </Box>
            ),
          },
        ]
      : []),
  ];

  const nestedColumns = [
    { title: "Award", dataIndex: "award", key: "award", width: 160 },
    {
      title: "File Name",
      dataIndex: "file_name",
      key: "file_name",
    },
    // { title: "Roles", dataIndex: "roles", key: "roles" },
    {
      title: "Show Disclaimer",
      dataIndex: "show_disclaimer",
      key: "show_disclaimer",
    },
    {
      title: "Configuration date",
      dataIndex: "configuration_date",
      key: "configuration_date",
    },
    {
      title: "Update date",
      dataIndex: "update_date",
      key: "update_date",
    },
    {
      title: "Download",
      key: "download",

      render: (_, row) =>
        row?.award_id != null ? (
          <Box display="flex" gap={1} alignItems="center">
            <IconButton
              size="small"
              onClick={() => handleDownloadTemplate(row)}
              sx={{ color: "var(--color-primary)" }}
              aria-label="Download template"
            >
              <DownloadIcon />
            </IconButton>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() => handlePrintWithDummyData(row)}
              sx={{
                color: "var(--color-primary)",
                borderColor: "var(--color-primary)",
                textTransform: "none",
                whiteSpace: "nowrap",
              }}
            >
              Print With Dummy Data
            </Button>
          </Box>
        ) : (
          "-"
        ),
    },
  ];

  const expandedRowRender = (record) => (
    <Table
      columns={nestedColumns}
      dataSource={record?.nestedData}
      pagination={false}
      rowKey={(row, index) => index}
      scroll={{ x: 910 }}
    />
  );

  // Filters are edited in the drawer and applied together (phase 2)
  const competitionOptionList = competitionFilterOptions.map((o) => ({
    value: o.value,
    option: o.label,
  }));
  const certificateTypeOptionList = certificateTypeFilterOptions.map((o) => ({
    value: o.value,
    option: o.label,
  }));
  const openFilters = () => {
    setFilterDraft({
      competition: filterCompetition?.value ?? "",
      certificateType: filterCertificateType ?? "",
    });
    setFiltering(true);
  };
  const applyFilters = () => {
    setFilterCompetition(
      competitionFilterOptions.find(
        (o) =>
          o.value == filterDraft.competition ||
          String(o.value) === String(filterDraft.competition)
      ) ?? null
    );
    setFilterCertificateType(
      filterDraft.certificateType === "" ? null : filterDraft.certificateType
    );
    setCurrentPage(1);
    setFiltering(false);
  };
  const resetFilters = () => {
    setFilterCompetition(null);
    setFilterCertificateType(null);
    setCurrentPage(1);
    setFiltering(false);
  };
  const filterChips = [
    filterCompetition && {
      key: "competition",
      label: "Competition",
      value: filterCompetition.label,
      onRemove: () => {
        setFilterCompetition(null);
        setCurrentPage(1);
      },
    },
    filterCertificateType && {
      key: "certificateType",
      label: "Certificate Type",
      value:
        certificateTypeFilterOptions.find((o) => o.value === filterCertificateType)
          ?.label ?? filterCertificateType,
      onRemove: () => {
        setFilterCertificateType(null);
        setCurrentPage(1);
      },
    },
  ].filter(Boolean);

  return (
    <Box className="wrapperBox simcc-page">
      <LoadingBackdrop loading={loading} />
      <PageHeader
        title="Certificate Templates"
        actions={
          isAdmin() && (
            <ReusableButton
              variant="primary"
              text="Add New Competition"
              iconType="add"
              onClick={() => setOnAddCompetition(true)}
            />
          )
        }
      />
      <TableCard
        toolbar={
          <TableToolbar
            search={
              <SearchInput
                state={searchText}
                setState={(v) => {
                  setSearchText(v);
                  setCurrentPage(1);
                }}
                placeholder="Search configuration or competition"
              />
            }
            onOpenFilters={openFilters}
            filtersOpen={filtering}
            filterCount={filterChips.length}
            meta={`${Number(totalItems).toLocaleString()} configurations`}
          />
        }
        chips={<FilterChips chips={filterChips} onClearAll={resetFilters} />}
      >
        <div className="simcc-antd-table simcc-antd-table--nested">
        <Table
          rowKey={(row, index) => row.id ?? index}
          columns={parentColumns}
          dataSource={formattedData}
          expandable={{ expandedRowRender, defaultExpandedRowKeys: ["0"] }}
          pagination={{
            current: currentPage,
            pageSize,
            total: totalItems,
            showSizeChanger: false,
            showTotal: (total) => `Total ${total} items`,
            onChange: (page, size) => {
              const newSize = size || 10;
              const pageSizeChanged = pageSizeRef.current !== newSize;
              if (pageSizeChanged) {
                setCurrentPage(1);
              } else {
                setCurrentPage(page);
              }
              setPageSize(newSize);
              pageSizeRef.current = newSize;
            },
          }}
          scroll={{ x: "fit-content" }}
          // style={{ width: "40%" }}
        />
        </div>
      </TableCard>
      <FilterDrawer
        open={filtering}
        onClose={() => setFiltering(false)}
        onReset={resetFilters}
        onApply={applyFilters}
      >
        <ReusableSelect
          compact
          type="filter"
          label="Competition"
          state={filterDraft.competition}
          setState={(value) => setFilterDraft((d) => ({ ...d, competition: value }))}
          options={competitionOptionList}
          loading={loadingFilters}
        />
        <ReusableSelect
          compact
          type="filter"
          label="Certificate Type"
          state={filterDraft.certificateType}
          setState={(value) =>
            setFilterDraft((d) => ({ ...d, certificateType: value }))
          }
          options={certificateTypeOptionList}
          loading={loadingFilters}
        />
      </FilterDrawer>
      <Grid>
        {/* Add New Competition Modal */}
        {isAdmin() && (
          <Modal open={onAddCompetition} onClose={() => cancelAddCompetition()}>
            <Box component="div" sx={{ outline: "none" }} tabIndex={-1}>
              <AddNewCompetitionForCertificateModal
                approveFunction={approveNewCompetitionFunction}
                cancelApprove={cancelAddCompetition}
              />
            </Box>
          </Modal>
        )}
        {/* Edit Competition Configuration Modal */}
        {isAdmin() && (
          <Modal
            open={onEditCompetition}
            onClose={() => cancelEditCompetition()}
          >
            <Box component="div" sx={{ outline: "none" }} tabIndex={-1}>
              <AddNewCompetitionForCertificateModal
                editMode
                configId={editingConfig?.id}
                approveFunction={approveEditCompetitionFunction}
                cancelApprove={cancelEditCompetition}
              />
            </Box>
          </Modal>
        )}
      </Grid>
      <Modal open={onDelete} onClose={() => setOnDelete(false)}>
        <ConfirmDeleteModal
          cancelDelete={() => setOnDelete(false)}
          deleteFunction={handleDeleteConfig}
          id={selectedConfigId}
          objectName="this configuration"
        />
      </Modal>
    </Box>
  );
};

export default ConfigureCertificate;
